/**
 * 완성본이 되려면 채워야 하는 칸. BE `incomplete()`(templates `mapping/resolve.py`)와 같은 판정이다.
 *
 * 필수 칸 표는 BE `mapping.required.json` 의 사본(`contract/mapping.required.json`)이다. 판정:
 * - 칸의 값이 null 이면 빈 칸이다. 문서에 없는 칸은 판정하지 않는다(열 수가 자세 종류와 맞지 않는 매핑 등).
 *   그런 매핑은 BE 가 저장 전에 거부한다.
 * - 노드 경로의 `*` 는 목록의 모든 항목이다. 노드가 null 이면(내보내지 않는 action) 그 노드의 규칙을 보지 않는다.
 * - `always` 는 늘 요구한다. `when_equals` 는 노드의 `field` 값이 `value` 일 때만 요구한다. `together` 는 모두
 *   비면 통과하고, 하나라도 채우면 나머지를 요구한다.
 * - `hands` · `cameras` 는 하나 이상이어야 한다(`hands.invalid` · `cameras.invalid`).
 * - issue 는 문서 순서다. 키 순서는 스키마 순서다. 최상위 키마다 "하나 이상" issue 가 그 키의 빈 칸보다 앞이다.
 *
 * BE 와 같은지는 `mappingRequired.cases.json`(BE 가 만든 기대값, `scripts/mappingRequiredCases.py`)이 본다.
 *
 * Owns:
 * - 사본의 모양 확인과 판정.
 *
 * Does not own:
 * - 사본 갱신 — `pnpm contract:sync`. 화면 문장 — `mappingIssueMessage.ts`. 묶어 보이기 — `mappingReview.ts`.
 */
import requiredTable from "../contract/mapping.required.json";
import { orderMappingKeys } from "../contract/mappingSchema";
import type { Mapping, MappingIssue } from "../domain/mapping";

type RequiredRule =
  | { readonly kind: "always" | "together"; readonly cells: readonly string[] }
  | {
      readonly kind: "when_equals";
      readonly field: string;
      readonly value: string;
      readonly cells: readonly string[];
    };

type RequiredNode = { readonly node: string; readonly rules: readonly RequiredRule[] };

/** 하나 이상이어야 하는 최상위 목록과 BE 원문. */
const NEEDS_ONE: Readonly<Record<string, string>> = {
  hands: "Select one single hand, or left and right hands",
  cameras: "Select one camera or more",
};

const REQUIRED: readonly RequiredNode[] = readRequired(requiredTable);

/**
 * 완성본이 되려면 고쳐야 하는 자리. 비어 있으면 일반 저장(`isTemp=false`)을 BE 가 받는다.
 *
 * @example 빈 매핑 → `robot_model.name` 등 모델 네 칸 · `hands.invalid` · `cameras.invalid`.
 */
export function requiredIssues(mapping: Mapping): MappingIssue[] {
  const document: unknown = orderMappingKeys(mapping);
  const gaps = new Map<string, MappingIssue>();
  for (const group of REQUIRED) {
    for (const node of nodePaths(document, group.node)) {
      const value = valueAt(document, node);
      if (value === null || value === undefined) {
        continue;
      }
      for (const rule of group.rules) {
        for (const issue of ruleGaps(document, node, rule)) {
          gaps.set(issue.path, issue);
        }
      }
    }
  }
  const issues: MappingIssue[] = [];
  if (!isObject(document)) {
    return issues;
  }
  for (const [key, value] of Object.entries(document)) {
    const needsOne = NEEDS_ONE[key];
    if (needsOne !== undefined && Array.isArray(value) && value.length === 0) {
      issues.push(issueOf(`${key}.invalid`, key, needsOne));
    }
    for (const path of leaves(value, key)) {
      const gap = gaps.get(path);
      if (gap !== undefined) {
        issues.push(gap);
      }
    }
  }
  return issues;
}

function ruleGaps(document: unknown, node: string, rule: RequiredRule): MappingIssue[] {
  if (rule.kind === "when_equals" && valueAt(document, joinPath(node, rule.field)) !== rule.value) {
    return [];
  }
  const empty = rule.cells.filter((cell) => valueAt(document, joinPath(node, cell)) === null);
  if (rule.kind === "together" && empty.length === rule.cells.length) {
    return [];
  }
  const message = ruleMessage(rule);
  return empty.map((cell) => issueOf("mapping.incomplete", joinPath(node, cell), message));
}

/** BE `_message` 와 같은 원문. 화면은 코드로 문장을 고르고 이 값은 기술 정보로만 보인다. */
function ruleMessage(rule: RequiredRule): string {
  switch (rule.kind) {
    case "always":
      return "Fill this cell";
    case "when_equals":
      return `Fill this cell when ${rule.field} is '${rule.value}'`;
    case "together":
      return "Fill all cells of this group, or fill none";
  }
}

function issueOf(code: string, path: string, message: string): MappingIssue {
  return {
    code,
    path,
    message,
    severity: "error",
    channel: null,
    otherChannel: null,
    row: null,
    timestamp: null,
    otherTimestamp: null,
    timestampUnit: null,
    otherTimestampUnit: null,
  };
}

// ── 점 경로 ────────────────────────────────────────────────────────────────

function joinPath(path: string, key: string | number): string {
  return path === "" ? String(key) : `${path}.${key}`;
}

/** 점 경로의 값. 경로가 문서에 없으면 `undefined` 다. 빈 경로는 문서 자신이다. */
function valueAt(document: unknown, path: string): unknown {
  let node = document;
  for (const key of path === "" ? [] : path.split(".")) {
    if (Array.isArray(node)) {
      node = /^\d+$/.test(key) ? (node as unknown[])[Number(key)] : undefined;
    } else if (isObject(node) && Object.prototype.hasOwnProperty.call(node, key)) {
      node = node[key];
    } else {
      return undefined;
    }
  }
  return node;
}

/** `*` 를 목록 순번으로 편 노드 경로. 예) `hands.*.action` → `hands.0.action` · `hands.1.action`. */
function nodePaths(document: unknown, pattern: string): string[] {
  let paths = [""];
  for (const key of pattern === "" ? [] : pattern.split(".")) {
    if (key !== "*") {
      paths = paths.map((path) => joinPath(path, key));
      continue;
    }
    paths = paths.flatMap((path) => {
      const items = valueAt(document, path);
      const count = Array.isArray(items) ? items.length : 0;
      return Array.from({ length: count }, (_, index) => joinPath(path, index));
    });
  }
  return paths;
}

/** 문서 순서의 leaf 경로. 빈 객체 · 빈 목록은 leaf 가 없다. */
function* leaves(node: unknown, path: string): Generator<string> {
  if (Array.isArray(node)) {
    for (const [index, value] of (node as unknown[]).entries()) {
      yield* leaves(value, joinPath(path, index));
    }
  } else if (isObject(node)) {
    for (const [key, value] of Object.entries(node)) {
      yield* leaves(value, joinPath(path, key));
    }
  } else {
    yield path;
  }
}

// ── 사본 읽기 ──────────────────────────────────────────────────────────────

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function malformed(field: string): Error {
  return new Error(`The required-cells contract copy is malformed: ${field}.`);
}

function readStrings(value: unknown, field: string): readonly string[] {
  if (!Array.isArray(value) || !value.every((item) => typeof item === "string")) {
    throw malformed(field);
  }
  return value as string[];
}

function readRule(value: unknown, field: string): RequiredRule {
  if (!isObject(value)) {
    throw malformed(field);
  }
  const cells = readStrings(value.cells, `${field}.cells`);
  if (value.kind === "always" || value.kind === "together") {
    return { kind: value.kind, cells };
  }
  if (
    value.kind === "when_equals" &&
    typeof value.field === "string" &&
    typeof value.value === "string"
  ) {
    return { kind: "when_equals", field: value.field, value: value.value, cells };
  }
  throw malformed(`${field}.kind`);
}

/** 사본의 모양을 확인한다. 모르는 규칙 종류는 계약 위반이다. 판정을 건너뛰면 BE 와 어긋난다. */
export function readRequired(value: unknown): readonly RequiredNode[] {
  if (!isObject(value) || !Array.isArray(value.nodes)) {
    throw malformed("nodes");
  }
  return (value.nodes as unknown[]).map((node, index) => {
    if (!isObject(node) || typeof node.node !== "string" || !Array.isArray(node.rules)) {
      throw malformed(`nodes.${index}`);
    }
    return {
      node: node.node,
      rules: (node.rules as unknown[]).map((rule, at) =>
        readRule(rule, `nodes.${index}.rules.${at}`),
      ),
    };
  });
}
